import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { format } from 'date-fns';
import { QueryEvent, QueryItem } from '@restore/queries/queries.types';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { StatusQuery } from '@components/Queries/StatusQyery';
import { QueryDropdown } from '@components/Queries/Dropdown';
import { UserRoles } from '@dynamika-shared/types/auth';

const loading = () => (
  <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300' />
);

const widths = {
  queryNumber: '10rem',
  status: '13rem',
  type: '20rem',
  siteId: '13rem',
  visitName: '13rem',
  patientCode: '15rem',
  created: '20rem',
  closed: '20rem',
  actions: '8rem',
};

const widthClasses = (name: keyof typeof widths) => ({
  thClassName: `w-[${widths[name]}] min-w-[${widths[name]}]`,
  tdClassName: `w-[${widths[name]}] min-w-[${widths[name]}]`,
});

// Shared columns factory for Queries tables (QC and SiteUser, etc.)
export type Handlers = {
  selectedRole: UserRoles;
  handleListQuerySubmit: (
    event: QueryEvent,
    query: QueryItem,
    data: {
      username: string | null | undefined;
      password: string | null | undefined;
      signAgreement: boolean | null | undefined;
      message: string;
      attachments: File[];
    }
  ) => void;
  updating: boolean;
};
export const queriesColumns = (
  handlers?: Handlers,
  siteIdToCode: Record<string, string> = {}
): ColumnType<QueryItem>[] => {
  const columns: ColumnType<QueryItem>[] = [
    {
      name: 'queryNumber',
      header: () => 'Query ID',
      getValue: row => (
        <CustomTooltip label={row.queryNumber} MAX_LABEL_LENGTH={12} />
      ),
      hasSorting: true,
      loading,
      ...widthClasses('queryNumber'),
    },
    {
      name: 'status',
      header: () => 'Status',
      hasSorting: true,
      getValue: row => <StatusQuery status={row.status} />,
      loading,
      ...widthClasses('status'),
    },
    {
      name: 'type',
      header: () => 'Type',
      ...widthClasses('type'),
      getValue: row => (
        <CustomTooltip label={row.types?.join(', ')} MAX_LABEL_LENGTH={20} />
      ),
      loading,
    },
    {
      name: 'siteId',
      header: () => 'Site',
      hasSorting: true,
      getValue: row => (
        <CustomTooltip
          label={siteIdToCode[row.siteId] ?? row.siteId ?? '-'}
          MAX_LABEL_LENGTH={12}
        />
      ),
      loading,
      ...widthClasses('siteId'),
    },
    {
      name: 'visitName',
      header: () => 'Visit',
      hasSorting: true,
      getValue: row => row.visitName ?? '-',
      loading,
      ...widthClasses('visitName'),
    },
    {
      name: 'patientCode',
      header: () => 'Subject',
      hasSorting: true,
      getValue: row => row.patientCode ?? '-',
      loading,
      ...widthClasses('patientCode'),
    },
    {
      name: 'created',
      header: () => 'Open Date',
      hasSorting: true,
      getValue: row =>
        row.created
          ? `${format(new Date(row.created), 'dd-MMM-yyy')} • ${format(
              new Date(row.created),
              'HH:mm:ss'
            )}`
          : '-',
      loading,
      ...widthClasses('created'),
    },
    {
      name: 'closed',
      header: () => 'Resolved Date',
      hasSorting: true,
      getValue: row =>
        row.closed
          ? `${format(new Date(row.closed), 'dd-MMM-yyy')} • ${format(
              new Date(row.closed),
              'HH:mm:ss'
            )}`
          : '-',
      loading,
      ...widthClasses('closed'),
    },
  ];

  if (!handlers) {
    return columns;
  }

  return [
    ...columns,
    {
      name: 'actions',
      header: () => 'Actions',
      headerClassName: 'flex items-center justify-center',
      ...widthClasses('actions'),
      getValue: row => (
        <QueryDropdown
          query={row}
          selectedRole={handlers.selectedRole}
          onSubmit={handlers.handleListQuerySubmit}
          updating={handlers.updating}
        />
      ),
      loading,
    },
  ];
};
